import { Form } from "antd"
import { Icon, notification, SelectInput, Text, TextInput } from "components/common"
import { IconText } from "components/custom"
import { useLazyGetProjectsByCityQuery } from "features/shared/api"
import {
  useLazyGetCompanyOfficesQuery,
  useLazyGetGoogleMeetingsQuery,
  useLazyGetOfficeCoordinatesQuery,
  useValidateGoogleMeetingMutation,
} from "features/tasks/api"
import { t } from "i18next"
import { OpenLocationCode } from "open-location-code"
import { useEffect, useMemo, useCallback, useState } from "react"
import { useSelector } from "react-redux"
import theme from "utils/themeVars"
import { getPlusCodeTooltip, isCompanyOffice, isRequiredSubofSubTask, isSiteOffice, isWalkInOrMeeting } from "../utilis"

const fieldMap = {
  walk_in: "companyOffice",
  company_office: "companyOffice",
  company_office_meeting_attempted: "companyOffice",
  outdoor: "plusCode",
  outdoor_meeting_attempted: "plusCode",
  site_office: "visitedProjects",
  site_office_meeting_attempted: "visitedProjects",
  google_meet: "google_meet",
}

const SubTaskAdditionalFields = ({ leadDetailInfo, rules, form }) => {
  const userInfo = useSelector(state => state?.app?.user.info)
  const isBayutPlatform = userInfo?.AgencyConfiguration?.Platform?.platformKey === "bayut_ksa"
  const cityId = userInfo?.City?.id?.toString()

  const task = Form.useWatch("task", form)
  const subTask = Form.useWatch("subTask", form)
  const subOfSubTask = Form.useWatch("subofSubTask", form)
  const plusCode = Form.useWatch("plusCode", form)
  const googleMeetingWatch = Form.useWatch("googleMeeting", form)

  const [getCompanyOffices, { data: companyOffice, isFetching: isFetchingCompanyOffices }] =
    useLazyGetCompanyOfficesQuery()
  const [getProjectsByCity, { data: projectsByCity, isFetching: isFetchingProjects }] = useLazyGetProjectsByCityQuery()
  const [getCoordinates] = useLazyGetOfficeCoordinatesQuery()
  const [getGoogleMeetings, { data: googleMeetings, isFetching: isFetchingGoogleMeetings }] =
    useLazyGetGoogleMeetingsQuery()
  const [validateGoogleMeeting, { isLoading: isValidatingGoogleMeeting }] = useValidateGoogleMeetingMutation()
  const [googleMeetingValidation, setGoogleMeetingValidation] = useState(null)

  useEffect(() => {
    if (!leadDetailInfo || !cityId) return

    if (leadDetailInfo.isLocationCompanyOffices) getCompanyOffices({ cityIds: cityId }, true)
    if (leadDetailInfo.isLocationSiteProjects) getProjectsByCity(cityId, true)
  }, [leadDetailInfo, cityId])

  useEffect(() => {
    if (!cityId) return
    if (isWalkInOrMeeting(subTask) || isCompanyOffice(subTask) || isCompanyOffice(subOfSubTask)) {
      getCompanyOffices({ cityIds: cityId }, true)
    }
    if (isSiteOffice(subOfSubTask)) {
      getProjectsByCity(cityId, true)
    }
  }, [subTask, subOfSubTask, cityId])

  useEffect(() => {
    if (isBayutPlatform && subOfSubTask === "google_meet") {
      getGoogleMeetings()
    }
  }, [subOfSubTask])

  useEffect(() => {
    setGoogleMeetingValidation(null)
    if (!googleMeetingWatch?.conferenceRecordName) {
      form.setFieldsValue({ googleVerified: false })
      return
    }
    const participants = [leadDetailInfo?.inquiry?.Client?.name, userInfo?.name].filter(Boolean)

    validateGoogleMeeting({
      conferenceRecordName: googleMeetingWatch?.conferenceRecordName,
      participants: participants.length > 0 ? participants : undefined,
    })
      .unwrap()
      .then(response => {
        setGoogleMeetingValidation(response)
        form.setFieldsValue({ googleVerified: response?.verified })
      })
      .catch(error => {
        notification.error({
          message: error?.message,
        })
        setGoogleMeetingValidation(null)
        form.setFieldsValue({ googleVerified: false })
      })
  }, [googleMeetingWatch, validateGoogleMeeting, leadDetailInfo, userInfo, t])

  const renderAdditionalField = useMemo(() => {
    if (!isRequiredSubofSubTask(subTask)) return null
    const key = subOfSubTask || subTask
    return fieldMap[key] || null
  }, [subTask, subOfSubTask])

  const handleLocateMe = () => {
    if (!navigator?.geolocation) {
      return notification.error({ message: t("Geolocation not supported by your browser") })
    }

    navigator.geolocation.getCurrentPosition(
      position => {
        const { latitude, longitude } = position.coords
        const code = new OpenLocationCode().encode(latitude, longitude)
        form.setFieldsValue({ plusCode: code })
      },
      error => notification.error({ message: error?.message }),
      { enableHighAccuracy: true, maximumAge: 0 }
    )
  }

  const renderCompanyOffice = (
    <Form.Item name="companyOffice" label={t("Company Office")} required rules={rules}>
      <SelectInput
        placeholder={t("Select company office")}
        options={companyOffice}
        inputLoading={isFetchingCompanyOffices}
        onChange={value => form.setFieldsValue({ companyOffice: value })}
        defaultValue={companyOffice?.length === 1 ? companyOffice[0]?.key : null}
      />
    </Form.Item>
  )

  const renderVisitedProjects = (
    <Form.Item name="visitedProject" label={t("Visited Project")} required rules={rules}>
      <SelectInput
        placeholder={t("Select visited project")}
        options={projectsByCity}
        inputLoading={isFetchingProjects}
        onChange={value => form.setFieldsValue({ visitedProject: value })}
      />
    </Form.Item>
  )

  const renderPlusCode = (
    <Form.Item
      name="plusCode"
      label={
        <>
          {t("Plus Code")}
          <Icon
            icon="BsInfo"
            color="#707070"
            size="1rem"
            style={{
              backgroundColor: "#E6E6E6",
              borderRadius: "1rem",
              marginLeft: 6,
              verticalAlign: "middle",
              "--icon-color": "#707070",
            }}
            tooltipText={getPlusCodeTooltip()}
            overlayInnerStyle={{ width: 360 }}
          />
        </>
      }
      required
      rules={rules.concat({
        validator: async (_, value) => {
          if (value) {
            const response = await getCoordinates(value)
            if (response?.error) {
              return Promise.reject(response?.error?.message)
            }
          }
          return Promise.resolve()
        },
      })}
    >
      <TextInput
        placeholder={t("Enter plus code")}
        allowClear
        onChange={() => form.validateFields(["plusCode"])}
        customSuffix={
          !plusCode && (
            <IconText
              icon="BiCurrentLocation"
              iconProps={{ color: theme["primary-color"], size: "1rem" }}
              title={t("Locate Me")}
              textType="secondary"
              titleProps={{
                className: "pointer",
                onClick: handleLocateMe,
              }}
            />
          )
        }
      />
    </Form.Item>
  )
  const renderValidationStatus = useCallback(() => {
    if (!googleMeetingValidation || !googleMeetingWatch?.conferenceRecordName) {
      return null
    }

    if (googleMeetingValidation.verified) {
      return (
        <div style={{ paddingTop: 4 }}>
          <IconText
            icon={"TiTick"}
            title={t("Selected Meeting is validated")}
            iconProps={{
              color: theme["primary-color"],
              hasBackground: true,
              iconContainerStyle: { padding: 2 },
            }}
            titleProps={{
              color: theme["primary-color"],
            }}
            style={{
              paddingTop: 4,
            }}
            gap={"8px"}
          />
        </div>
      )
    }

    return (
      <div style={{ paddingTop: 4 }}>
        <IconText
          icon={"BsInfo"}
          title={t("Meeting is invalid: Select another meeting")}
          iconProps={{
            color: theme["error-color"],
            hasBackground: true,
            iconContainerStyle: { padding: 2 },
          }}
          titleProps={{
            color: theme["error-color"],
          }}
          gap={"4px"}
        />
      </div>
    )
  }, [googleMeetingValidation, googleMeetingWatch, t])

  const renderGoogleMeetings = (
    <>
      <Form.Item name="googleMeeting" label={t("Meeting")} rules={rules} extra={renderValidationStatus()}>
        <SelectInput
          placeholder={t("Select Google Meeting")}
          options={googleMeetings}
          inputLoading={isFetchingGoogleMeetings || isValidatingGoogleMeeting}
          getOptionValue={option => option?.conferenceRecordName}
          onChange={(value, option) => {
            form.setFieldsValue({
              googleMeeting: option,
            })
          }}
          allowClear
        />
      </Form.Item>
      <Form.Item name="googleVerified" noStyle>
        <input type="hidden" />
      </Form.Item>
    </>
  )
  switch (renderAdditionalField) {
    case "companyOffice":
      return renderCompanyOffice
    case "visitedProjects":
      return renderVisitedProjects
    case "plusCode":
      return renderPlusCode
    case "google_meet":
      return isBayutPlatform  ? renderGoogleMeetings : null
    default:
      return null
  }
}

export default SubTaskAdditionalFields
